/**
 * The shell: the header and its language switch, the prototype banner, the
 * section rail, the opening block and the photograph dialog of §5.8. The quiet
 * sticky summary of §5.4 was here until `082_offer_first`; the offer card of
 * `build/FIXES.md` item 76 replaced it and carries every one of its rows.
 */

import { useEffect, useRef, useState } from 'react'

import { brandTagline } from '@plt/emails'

import { TEST_SYSTEM_NOTICE, catalogueUnverified } from '../lib/catalogueMode.ts'
import { isProduction, readPublicConfig } from '../lib/env.ts'
import type { PageBundle } from './bundle.ts'
import type { Lang } from './lang.ts'
import { HERO_SHOT, STRIP_ALTS, STRIP_SHOTS, strip640 } from './catalogue.ts'
import { DishName } from './components.tsx'
import { nameMarksIn } from './model.ts'
import { PlanteenLogo } from './logo.tsx'
import { RAIL_SECTIONS, RAIL_TICK_LABEL, activeSection, type RailSection } from './rail.ts'

/* ------------------------------------------------------------------ *
 * The header
 * ------------------------------------------------------------------ */

export function Header(props: { page: PageBundle }): JSX.Element {
  const { t, state, set } = props.page
  return (
    <header className="top">
      <PlanteenLogo lang={state.lang} />
      {/* DECIDED 20261019, Barna: the header, the legal pages and every
          email — header and footer both — say the one line, from
          `BRAND_TAGLINE` in `@plt/emails`. */}
      <span className="kicker">{brandTagline(state.lang)}</span>
      <span className="spacer" />
      <button
        className="chip"
        id="lang"
        type="button"
        aria-pressed={state.lang === 'en'}
        aria-label={t('Nyelv', 'Language')}
        onClick={() => set({ lang: state.lang === 'hu' ? 'en' : 'hu' })}
      >
        {state.lang === 'hu' ? 'EN' : 'HU'}
      </button>
    </header>
  )
}

/**
 * The banner says what this page is. §7.3: nothing in the catalogue is
 * verified yet, and the allergen blocks on a test database are a test
 * marking, and while `CATALOGUE_ALLOW_UNVERIFIED` is on the allergens are
 * being verified. The sentence names what is not verified rather than hedging the
 * whole page, because a customer has to be able to tell which part to doubt.
 */
export function Banner(props: { page: PageBundle; appEnv?: string }): JSX.Element | null {
  const { t, context } = props.page
  const testMarked = context.dishes.some(
    (dish) => (dish as { allergen_test_marked?: boolean }).allergen_test_marked === true,
  )
  /* Production with a verified catalogue (20261101): no banner at all. The
     plain test line is false there, and the two warnings above it still show
     if production ever met an unverified or test-marked catalogue. */
  const appEnv = props.appEnv ?? readPublicConfig().appEnv
  /* launch_notice (DECIDED 20261101): where production shows no banner, the
     launch notice takes the band. Test keeps its banner and never shows both. */
  if (isProduction({ ...readPublicConfig(), appEnv }) && !catalogueUnverified(context) && !testMarked) {
    return <LaunchNotice page={props.page} />
  }
  return (
    <div className="banner" id="banner">
      {catalogueUnverified(context)
        ? t(TEST_SYSTEM_NOTICE.hu, TEST_SYSTEM_NOTICE.en)
        : testMarked
        ? t(
            'Teszt környezet — az árak, a kínálat, a gyümölcs tételek és az allergének még nincsenek hitelesítve.',
            'Test environment — prices, selection, fruit items and allergens are not yet verified.',
          )
        : t(
            'Teszt környezet. Itt leadott rendelés nem valódi megrendelés.',
            'Test environment. An order placed here is not a real order.',
          )}
    </div>
  )
}

/**
 * **The launch notice (DECIDED 20261101, wording PROPOSED).** One quiet line
 * under the header saying the page is new and where to send feedback. The
 * text is the register's — `launch_notice_hu` / `launch_notice_en`, plain
 * text — so it can be reworded or switched off without a deploy; an empty
 * value hides it. The first e-mail address in it is the one link, a mailto.
 */
const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/

export function LaunchNotice(props: { page: PageBundle }): JSX.Element | null {
  const { page } = props
  const text = page.param(page.state.lang === 'en' ? 'launch_notice_en' : 'launch_notice_hu').trim()
  if (text === '') return null
  const match = EMAIL_IN_TEXT.exec(text)
  return (
    <div className="launch" id="launchNotice" role="note">
      <p>
        {match === null ? (
          text
        ) : (
          <>
            {text.slice(0, match.index)}
            <a href={`mailto:${match[0]}`}>{match[0]}</a>
            {text.slice(match.index + match[0].length)}
          </>
        )}
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The step rail and the opening block
 * ------------------------------------------------------------------ */

/**
 * **Specification §3.1, *Section rail* (DECIDED 20261023).** The rail that was
 * here — four numbered step links, 01–04 — is the one this builds on, not a
 * second one beside it:
 *
 * - The four sections under their own page headings: no new labels, no
 *   numbers (`RAIL_SECTIONS`).
 * - The section on screen is highlighted, from an `IntersectionObserver`, and
 *   carries `aria-current="location"`.
 * - A click scrolls to the section and moves focus to its heading, smoothly
 *   only where motion is wanted.
 * - A section shows a tick, with an accessible name in both languages, while
 *   every field it requires is valid (`sectionTicks`).
 * - Below the desktop breakpoint the same element is a slim sticky line under
 *   the header: the current heading and its position, *2/4*. The links are
 *   hidden there and the stylesheet keeps the line out of the keyboard's way.
 */
export function Rail(props: { page: PageBundle }): JSX.Element {
  const { t, ticks } = props.page
  const [active, setActive] = useState<RailSection['id']>('sec1')
  useActiveSection(setActive)
  const index = Math.max(
    0,
    RAIL_SECTIONS.findIndex((section) => section.id === active),
  )
  const current = RAIL_SECTIONS[index]!
  return (
    <nav className="rail" id="rail">
      {RAIL_SECTIONS.map((section) => {
        const done = ticks[section.id]
        return (
          <a
            key={section.id}
            href={`#${section.id}`}
            data-rail={section.id}
            aria-current={section.id === active ? 'location' : undefined}
            className={done ? 'done' : undefined}
            onClick={(event) => {
              event.preventDefault()
              setActive(section.id)
              goToSection(section.id)
            }}
          >
            <span className="railname">{t(section.heading.hu, section.heading.en)}</span>
            {done ? (
              <span
                className="railtick"
                data-railtick={section.id}
                role="img"
                aria-label={t(RAIL_TICK_LABEL.hu, RAIL_TICK_LABEL.en)}
              >
                ✓
              </span>
            ) : null}
          </a>
        )
      })}
      <p className="railline" id="railLine" data-railline={current.id}>
        <span className="railname">{t(current.heading.hu, current.heading.en)}</span>
        <span className="railpos num">
          {index + 1}/{RAIL_SECTIONS.length}
        </span>
      </p>
    </nav>
  )
}

const wantsNoMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Scroll to a section and put focus on its heading (§3.1). */
export function goToSection(id: string, doc: Document = document): void {
  const section = doc.getElementById(id)
  if (section === null) return
  section.scrollIntoView?.({ behavior: wantsNoMotion() ? 'auto' : 'smooth', block: 'start' })
  const heading = section.querySelector<HTMLElement>('h2')
  if (heading === null) return
  /* A heading is not focusable of itself; -1 lets a script focus it without
     putting it in the Tab order. */
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
  heading.focus({ preventScroll: true })
}

/**
 * The section on screen, from an IntersectionObserver over the four sections.
 * The band it watches starts under the header (`--jump`) and ends at 55 % of
 * the viewport, so a section counts once its top has come up into the upper
 * part of the screen. The sections are looked up after every render and the
 * observer is only rebuilt when one of them is a different node — steps 02
 * and 04 are replaced while there is no headcount.
 */
function useActiveSection(onActive: (id: RailSection['id']) => void): void {
  const watched = useRef<{ nodes: (Element | null)[]; observer: IntersectionObserver | null }>({
    nodes: [],
    observer: null,
  })
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const nodes = RAIL_SECTIONS.map((section) => document.getElementById(section.id))
    const same =
      watched.current.observer !== null &&
      nodes.every((node, index) => node === watched.current.nodes[index])
    if (same) return
    watched.current.observer?.disconnect()
    const visible = new Set<string>()
    const passed = new Set<string>()
    const jump = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--jump')) || 0
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id
          if (entry.isIntersecting) visible.add(id)
          else visible.delete(id)
          const bandTop = entry.rootBounds?.top ?? jump
          if (entry.boundingClientRect.top < bandTop) passed.add(id)
          else passed.delete(id)
        }
        const id = activeSection(visible, passed)
        if (id !== null) onActive(id)
      },
      { rootMargin: `-${Math.round(jump)}px 0px -45% 0px`, threshold: 0 },
    )
    for (const node of nodes) if (node !== null) observer.observe(node)
    watched.current = { nodes, observer }
  })
  useEffect(
    () => () => {
      watched.current.observer?.disconnect()
      watched.current.observer = null
    },
    [],
  )
}

/**
 * **Run 19 §10 (DECIDED 20261016, Barna): the intro text.**
 *
 * A short text at the top of the page for somebody who has never ordered
 * catering and does not know what the page will do for them. It is held as
 * `page_intro_hu` and `page_intro_en` in the register — not here — so the
 * B71 copy round can change it without a deployment, and the wording in the
 * register today is the plan's own **placeholder**.
 *
 * `null` where the register has no text, and then the page reads exactly as
 * it did: an empty parameter hides what depends on it, as every other
 * *needed* value does.
 */
export const pageIntroText = (page: PageBundle): string | null => {
  const key = page.state.lang === 'en' ? 'page_intro_en' : 'page_intro_hu'
  const text = page.param(key).trim()
  return text === '' ? null : text
}

/**
 * **Intro follow-up of 24 October 2026: no break inside the claim.**
 *
 * A browser may break a line after a hyphen, so *100%-ban* and *plant-based*
 * split across two lines on a narrow screen. The word that carries the
 * hyphen is kept on one line at render time, in a `white-space: nowrap`
 * span. The register's text is not touched: the claim tests read the
 * stored value, and the page shows the same characters.
 */
export const CLAIM_NOWRAP: Readonly<Record<Lang, string>> = Object.freeze({ hu: '100%-ban', en: 'plant-based' })

export const keepClaimTogether = (text: string, lang: Lang): Array<string | JSX.Element> => {
  const claim = CLAIM_NOWRAP[lang]
  const parts = text.split(claim)
  return parts.flatMap((part, index) =>
    index === 0
      ? [part]
      : [
          <span className="claimnowrap" key={index}>
            {claim}
          </span>,
          part,
        ],
  )
}

/**
 * **The opening band (redesign, DECIDED 20261101).** Black, two columns: the
 * claim as a yellow kicker (`brandTagline`, the one brand line), the
 * headline, the intro from the register, one yellow button to step 1, and the
 * landscape mixed box on a yellow backing card with its caption. Below 860px
 * it stacks, photo under the text. The copy is the page's own; the button,
 * caption and alt text are the new strings the design needs.
 */
export function Hero(props: { page: PageBundle }): JSX.Element {
  const { t, state } = props.page
  /* Run 19 §10: *the existing 100% plant-based statement at the top of the
     page is merged into this text rather than shown twice.* The intro's own
     last sentence carries the claim, in the receipt email's words (§14), so
     the hero line stands only while there is no intro to carry it. */
  const intro = pageIntroText(props.page)
  return (
    <section className="hero" id="hero">
      <div className="herowrap">
        <div className="herotext">
          <span className="kicker" id="heroKicker">{brandTagline(state.lang)}</span>
          <h1 id="heroH">{t('Terülj, terülj, asztalkám!', 'The table everyone gathers around.')}</h1>
          {/* Run 10 item 7a, the first of the three places: near the headline,
              calm, one line. Run 19 §10 merges it into the intro text where
              there is one. */}
          {intro === null ? (
            <p className="pbline" id="heroPb" data-testid="plant-based-hero">
              {t('Minden termékünk 100%-ban növényi.', 'Everything we serve is 100% plant-based.')}
            </p>
          ) : null}
          {/* Intro follow-up of 24 October 2026: the intro now says what this
              line says (we suggest the quantities), so the line stands only while
              the register holds no intro text, as the plant-based line does. */}
          {intro === null ? (
            <p className="lede" id="heroS">
              {t(
                'Ön kiválasztja, mit szeretne. Mi kiszámoljuk a javasolt mennyiséget, hogy mindenki jól érezze magát.',
                'You choose what you would like. We work out the recommended quantity so that everyone has a good time.',
              )}
            </p>
          ) : null}
          <p id="heroP">
            {t(
              /* Run 10 item 7a: the plant-based sentence is the line under the
                 headline now, so it is not said twice in the hero. */
              'Wrapek, szendvicsek, kanapék és házi sütemények, amelyek a rendezvénye fénypontjai lesznek.',
              'Wraps, sandwiches, canapés and home-made cakes that become the highlight of your event.',
            )}
          </p>
          {/* Run 19 §10: the intro, the register's text in the page's
              language, and never a string held here. */}
          {intro === null ? null : (
            <p className="pageintro" id="pageIntro" data-testid="page-intro">
              {keepClaimTogether(intro, props.page.state.lang)}
            </p>
          )}
          <div className="ctas">
            <button type="button" className="cta" id="heroGo" onClick={() => goToSection('sec1')}>
              {t('Állítsa össze a menüt →', 'Build your menu →')}
            </button>
          </div>
        </div>
        <figure className="shot" id="heroShot">
          <img
            src={HERO_SHOT.file}
            srcSet={`${HERO_SHOT.small} 800w, ${HERO_SHOT.file} 1448w`}
            sizes="(max-width: 860px) 92vw, 560px"
            width={1448}
            height={1086}
            alt={t.of(HERO_SHOT.alt)}
            fetchPriority="high"
          />
          <figcaption>{t.of(HERO_SHOT.caption)}</figcaption>
        </figure>
      </div>
    </section>
  )
}

/**
 * **The trust bar (redesign, DECIDED 20261101 for the 300-guest line).** The
 * items are the register's — `trust_bar_hu` / `trust_bar_en`, each a JSON list
 * of `[bold, line]` pairs — so no claim is held in code. One to four items,
 * two by two on phones; nothing at all when the register has none.
 */
export function trustBarItems(page: PageBundle): Array<[string, string]> {
  const raw = page.param(page.state.lang === 'en' ? 'trust_bar_en' : 'trust_bar_hu').trim()
  if (raw === '') return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((item): item is [string, string] =>
        Array.isArray(item) && typeof item[0] === 'string' && typeof item[1] === 'string')
      .slice(0, 4)
  } catch {
    return []
  }
}

export function TrustBar(props: { page: PageBundle }): JSX.Element | null {
  const items = trustBarItems(props.page)
  if (items.length === 0) return null
  return (
    <div className="trust" id="trust" data-items={items.length}>
      <div className="trustwrap">
        {items.map(([bold, line], index) => (
          <div key={index} className="trustitem">
            <b>{bold}</b>
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * **The photo strip (redesign, DECIDED 20261101, run prompt §C.3).** Three
 * event photos, the first in the wide column, each with its caption pill.
 * Shown while `menu_show_overview` says so, as before.
 */
export function Strip(props: { page: PageBundle }): JSX.Element {
  const { t, overview } = props.page
  if (!overview) return <div id="strip" />
  const shots = STRIP_SHOTS.filter((shot) => shot.file !== null)
  return (
    <div id="strip">
      <div className="strip" style={{ ['--shots' as string]: shots.length }}>
        {/* Fix list item 1 (sweep observation 4): a shot with no photograph
            is left out rather than shown as a *Fotó hiányzik* placeholder. */}
        {shots.map((shot, index) => (
          <figure className="ph" key={index} data-shot={index + 1}>
            <img
              src={shot.file ?? ''}
              srcSet={`${strip640(shot.file ?? '')} 640w, ${shot.file ?? ''} 1200w`}
              sizes={index === 0 ? '(max-width: 760px) 92vw, 460px' : '(max-width: 760px) 46vw, 330px'}
              alt={t.of(STRIP_ALTS[index] ?? shot.caption)}
              loading="lazy"
              decoding="async"
            />
            <figcaption>{t.of(shot.caption)}</figcaption>
          </figure>
        ))}
      </div>
      <p className="sub">
        {t(
          'Példa: a tényleges összeállítás a választott ételektől függ.',
          'Example: the actual selection depends on the dishes you choose.',
        )}
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * §5.4, the quiet sticky summary — moved, not deleted
 * ------------------------------------------------------------------ */

/* `StickySummary()` stood here until `082_offer_first`. Ruling 7 of 8 October
   2026 and `build/FIXES.md` item 76 replace the side summary with the offer
   card, which takes the same slot in the right column and the same `.summary`
   and `#sumBody` the stylesheet draws: every row of this function moved into
   `OfferTotals()` in `sections/Offer.tsx`, unchanged, and nothing was lost.
   `build/STATUS.md` §4 has the field-by-field mapping. */

/* ------------------------------------------------------------------ *
 * §5.8, the photograph (build/FIXES.md item 29)
 * ------------------------------------------------------------------ */

/**
 * The open photograph is state, so it follows the language instead of keeping
 * the one it was opened in. A *Bezárás* / *Close* button, focus moved to it
 * on opening, Tab trapped inside the dialog, Escape and the button both
 * returning focus to the dish row the photograph came from, and `aria-label`
 * naming the dish.
 *
 * DR-6's lock leaves the close button alone, or a customer who submitted with
 * a photograph open could not get rid of it.
 */
export function Zoom(props: { page: PageBundle }): JSX.Element {
  const { t, state, dishes, closeZoom } = props.page
  const dialog = useRef<HTMLDivElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const dish = state.zoom === null ? undefined : dishes.find((candidate) => candidate.id === state.zoom)
  const photo = dish?.photo ?? null

  useEffect(() => {
    if (photo !== null) closeButton.current?.focus()
  }, [photo === null, state.zoom])

  useEffect(() => {
    if (state.zoom === null) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeZoom()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialog.current?.querySelectorAll('button') ?? [])].filter(
        (button) => !button.disabled,
      )
      if (focusable.length === 0) return
      event.preventDefault()
      const index = focusable.indexOf(document.activeElement as HTMLButtonElement)
      const next =
        ((index < 0 ? 0 : index) + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length
      focusable[next]?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [state.zoom, closeZoom])

  if (dish === undefined || photo === null) {
    return <div className="zoom hide" id="zoom" role="dialog" aria-modal="true" />
  }
  const label = t(dish.nameHu, dish.nameEn)
  return (
    <div
      className="zoom"
      id="zoom"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      ref={dialog}
      /* Anywhere inside the dialog closes it, the close button included —
         that is what the photograph did before item 29, and the button only
         says so out loud. */
      onClick={closeZoom}
    >
      <figure>
        <img src={photo.large} alt={label} width={photo.w ?? undefined} height={photo.h ?? undefined} />
        <figcaption>
          <span>
            <DishName label={label} marks={nameMarksIn(dish, t.lang)} />
          </span>
          <button type="button" className="zclose" id="zoomClose" ref={closeButton}>
            {t('Bezárás', 'Close')}
          </button>
        </figcaption>
      </figure>
    </div>
  )
}
